import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import './OnboardWizard.css';

// Import step components
import ConfigureMpxChannelStep from './add-channel/ConfigureMpxChannelStep';
import ConfigureMpxListenerStep from './add-channel/ConfigureMpxListenerStep';
import PublishMpxStep from './add-channel/PublishMpxStep';
import ConfirmStep from './add-channel/ConfirmStep';
import CompleteStep from './add-channel/CompleteStep';

// Define wizard steps for MPX
type WizardStep = 'configure' | 'listener' | 'publish' | 'confirm' | 'complete';

interface WizardState {
  currentStep: WizardStep;
  channelConfig: any;
  // Store configure step form state to preserve across navigation
  configureFormState: {
    selectedHostPort: string;
    selectedPrefix: string;
    customPath: string;
    name: string;
    description: string;
    target_endpoint: string;
    mediator_id: string;
  } | null;
  // Store listener configuration separately
  listenerFormState: {
    selectedHostPort: string;
    selectedPrefix: string;
    customPath: string;
    name: string;
    description: string;
    target_endpoint: string;
  } | null;
  mpxPublishConfig: {
    connectionPhrase: string;
    headline: string;
    description: string;
    expiryDate: string | null;
    noExpiry: boolean;
    limitUses: number | null;
    noLimit: boolean;
  } | null;
}

const AddMpxChannelWizardPage: React.FC = () => {
  const navigate = useNavigate();
  const { actions } = useApp();
  
  const [wizardState, setWizardState] = useState<WizardState>({
    currentStep: 'configure',
    channelConfig: null,
    configureFormState: null,
    listenerFormState: null,
    mpxPublishConfig: null,
  });

  const steps: { key: WizardStep; label: string }[] = [
    { key: 'configure', label: 'Configure' },
    { key: 'listener', label: 'Listener' },
    { key: 'publish', label: 'Publish' },
    { key: 'confirm', label: 'Confirm' },
    { key: 'complete', label: 'Complete' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === wizardState.currentStep);

  const goToStep = (step: WizardStep) => {
    setWizardState(prev => ({ ...prev, currentStep: step }));
  };

  const nextStep = () => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      goToStep(steps[nextIndex].key);
    }
  };

  const prevStep = () => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      goToStep(steps[prevIndex].key);
    }
  };

  const handleConfigurationComplete = (config: any, formState?: any) => {
    setWizardState(prev => ({ 
      ...prev, 
      channelConfig: config,
      configureFormState: formState || prev.configureFormState
    }));
    nextStep();
  };

  const handleListenerConfigComplete = (config: any, formState?: any) => {
    setWizardState(prev => ({ 
      ...prev, 
      channelConfig: { 
        ...prev.channelConfig, 
        ...config 
      },
      listenerFormState: formState || prev.listenerFormState
    }));
    nextStep();
  };

  const handlePublishConfigComplete = (config: WizardState['mpxPublishConfig']) => {
    setWizardState(prev => ({ ...prev, mpxPublishConfig: config }));
    nextStep();
  };

  const handleChannelCreated = (config: any) => {
    setWizardState(prev => ({ ...prev, channelConfig: config }));
    nextStep();
  };

  const handleFinish = () => {
    navigate('/channels');
  };

  const handleConfigureChannel = () => {
    if (wizardState.channelConfig?.config_id) {
      navigate(`/channels/${wizardState.channelConfig.config_id}`);
    }
  };

  const handleCancel = () => {
    navigate('/channels');
  };

  // Render current step
  const renderStep = () => {
    switch (wizardState.currentStep) {
      case 'configure':
        return (
          <ConfigureMpxChannelStep
            channelConfig={wizardState.channelConfig}
            configureFormState={wizardState.configureFormState}
            onConfigChange={handleConfigurationComplete}
            onBack={handleCancel}
          />
        );
      case 'listener':
        return (
          <ConfigureMpxListenerStep
            channelConfig={wizardState.channelConfig}
            listenerFormState={wizardState.listenerFormState}
            onConfigChange={handleListenerConfigComplete}
            onBack={prevStep}
          />
        );
      case 'publish':
        return (
          <PublishMpxStep
            publishConfig={wizardState.mpxPublishConfig}
            tenantId={wizardState.channelConfig?.tenant_id || ''}
            onConfigChange={handlePublishConfigComplete}
            onBack={prevStep}
          />
        );
      case 'confirm':
        return (
          <ConfirmStep
            protocol="mpx"
            channelConfig={wizardState.channelConfig}
            mpxPublishConfig={wizardState.mpxPublishConfig}
            onChannelCreated={handleChannelCreated}
            onBack={prevStep}
          />
        );
      case 'complete':
        return (
          <CompleteStep
            channelConfig={wizardState.channelConfig}
            onFinish={handleFinish}
            onConfigureChannel={handleConfigureChannel}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-network-wired mr-2"></i>
          Add MPX Channel
        </h1>
        <button
          className="btn btn-sm btn-secondary"
          onClick={handleCancel}
        >
          <i className="fas fa-times mr-1"></i>
          Cancel
        </button>
      </div>

      {/* Progress Bar */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="wizard-progress">
            <div className="wizard-steps">
              {steps.map((step, index) => (
                <div
                  key={step.key}
                  className={`wizard-step ${
                    index === currentStepIndex
                      ? 'active'
                      : index < currentStepIndex
                      ? 'completed'
                      : ''
                  }`}
                >
                  <div className="wizard-step-circle">
                    {index < currentStepIndex ? (
                      <i className="fas fa-check"></i>
                    ) : (
                      <span>{index + 1}</span>
                    )}
                  </div>
                  <div className="wizard-step-label">{step.label}</div>
                  {index < steps.length - 1 && <div className="wizard-step-line"></div>}
                </div>
              ))}
            </div>
          </div>
          
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {renderStep()}
      </div><br/>
    </div>
  );
};

export default AddMpxChannelWizardPage;
